import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAdvisorStore } from './advisorStore.js';

export const useEntityStore = defineStore('entity', () => {
  const entity = ref(null);
  const isFetching = ref(false);
  const hasFetched = ref(false);
  const currentId = ref(null);

  // CRM state
  const crmContact = ref(null);
  const isFetchingCrmContact = ref(false);
  const hasFetchedCrmContact = ref(false);

  // API
  async function fetchEntity(externalId = currentId.value) {
    if (!externalId) {
      console.error('No externalId provided and no currentId set');
      return;
    }

    isFetching.value = true;
    currentId.value = externalId;

    try {
      const res = await fetch(`/api/v1/entities/${externalId}/`);

      if (!res.ok) {
        const error = new Error('Failed to load entity');

        // attach extra context directly on the error
        error.extra = {
          externalId,
          status: res.status,
          statusText: res.statusText,
          url: res.url,
        };

        throw error;
      }

      const data = await res.json();

      if (currentId.value === externalId) {
        entity.value = data;
        clearCrmContact();
      }
    } catch (error) {
      if (currentId.value === externalId) {
        Sentry.captureException(error, {
          extra: {
            externalId,
            currentId: currentId.value,
            ...(error.extra || {}),
          },
        });

        console.error(error, 'Failed to load entity');
        entity.value = null;
        clearCrmContact();
      }
    } finally {
      if (currentId.value === externalId) {
        hasFetched.value = true;
        isFetching.value = false;
      }
    }
  }

  async function fetchCrmContact() {
    if (!entity.value?.crm_type || !entity.value?.crm_connection_id) {
      hasFetchedCrmContact.value = true;
      return null;
    }

    isFetchingCrmContact.value = true;
    const advisorStore = useAdvisorStore();

    try {
      const response = await fetch(
          `/api/v1/crm/${entity.value.crm_type.toLowerCase()}/contact/${entity.value.crm_connection_id}/`,
          {
            headers: advisorStore.headers,
          },
      );

      if (!response.ok) {
        const error = new Error(`Error fetching CRM contact: ${response.statusText}`);

        Sentry.captureException(error, {
          extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            crmType: entity.value.crm_type,
            crmConnectionId: entity.value.crm_connection_id,
            entityId: entity.value.id,
          },
        });

        console.error('Error fetching CRM contact:', response.statusText);
        return null;
      }

      const data = await response.json();
      crmContact.value = data;
      return data;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          crmType: entity.value?.crm_type,
          crmConnectionId: entity.value?.crm_connection_id,
          entityId: entity.value?.id,
        },
      });

      console.error('Error fetching CRM contact:', error);
      return null;
    } finally {
      isFetchingCrmContact.value = false;
      hasFetchedCrmContact.value = true;
    }
  }

  // Computed
  const canViewEntity = computed(() =>
    entity.value?.user_permissions?.includes('view_entity') ?? false,
  );

  const canChangeEntity = computed(() =>
    entity.value?.user_permissions?.includes('change_entity') ?? false,
  );

  const hasPermission = computed(() =>
    canViewEntity.value || canChangeEntity.value,
  );

  const portalStatusBadge = computed(() => {
    const status = entity.value?.portal_status;

    if (!status) {
      return { status: 'info', text: 'Loading...' };
    }

    switch (status) {
      case 'not_invited':
        return { status: 'warning', text: 'Not Invited' };
      case 'invited_pending':
        return { status: 'warning', text: 'Pending Activation' };
      case 'activated':
        return { status: 'success', text: 'Active' };
      default:
        return { status: 'warning', text: 'Not Invited' };
    }
  });

  // Utilities
  function clearEntity() {
    entity.value = null;
    isFetching.value = false;
    hasFetched.value = false;
    currentId.value = null;

    clearCrmContact();
  }

  function clearCrmContact() {
    crmContact.value = null;
    isFetchingCrmContact.value = false;
    hasFetchedCrmContact.value = false;
  }

  function updateName(first, last) {
    if (!entity.value) return;

    entity.value.name = last ? `${first} ${last}` : first;
  }

  return {
    // Entity
    entity,
    currentId,
    isFetching,
    hasFetched,
    canViewEntity,
    canChangeEntity,
    hasPermission,
    portalStatusBadge,

    fetchEntity,
    clearEntity,
    updateName,

    // CRM
    crmContact,
    isFetchingCrmContact,
    hasFetchedCrmContact,

    fetchCrmContact,
    clearCrmContact,
  };
});
